import { useTranslation } from 'contexts/Localization'
import React from 'react'
import styled, { css, FlattenInterpolation, ThemeProps } from 'styled-components'
import { Text, Button, Flex } from 'vvs-uikit'
import { MAX_NFT_SELECTABLE, MAX_NUM_OF_BOOST_FARM } from 'config/constants/farmBoost'
import { LegendaryColor, EpicColor, RareColor, SpecialColor } from 'views/Farms/styles'
import { Path } from 'config/constants/paths'
import history from 'routerHistory'
import { getVVSMinerMoleMintedUrl } from 'utils/mintedNftCollectionUrls'

const CommonColor = css`
  color: ${({ theme }) => theme.colors.textSubtle};
`

export const DescTextStyles = css`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  font-family: 'Soleil';
`

const Content = styled.div`
  padding: 40px 24px 24px 24px;
`

const StepsContent = styled.div`
  display: flex;
  gap: 16px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.boostDivider};
  padding-bottom: 40px;
  flex-direction: column;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  }
`

const StepCard = styled.div`
  flex: 1;
  padding: 18px;
  border: 1px solid ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${({ theme }) => theme.mediaQueries.md} {
    max-width: 300px;
    min-height: 300px;
  }
`

const StepTitle = styled(Text)`
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
`

const StepSubtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.boostBlueText};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`

const DescText = styled(Text)<{ extraStyle?: FlattenInterpolation<ThemeProps<any>> }>`
  ${DescTextStyles}
  color: ${({ theme }) => theme.colors.textSubtle};
  ${(props) => props.extraStyle}
`

const StepButtonWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: 10px;
`
const StakeDetailsContent = styled.div`
  display: flex;
  gap: 40px;
  padding: 40px 0;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.md} {
    flex-direction: row;
  }
`

const StakeDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ theme }) => theme.mediaQueries.md} {
    max-width: 300px;
  }
`

const StakeDetailsTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  font-family: 'Soleil';
  color: ${({ theme }) => theme.colors.text};
`

const QuestionBanner = styled.div`
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.boostDivider};
  border-radius: 12px;
  position: relative;
`

const QuestionTextStyle = css`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  font-family: 'Soleil';
  display: inline;
`

const QuestionTitle = styled(Text)`
  ${QuestionTextStyle}

  >a {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`

const QuestionBannerMole = styled.img`
  position: absolute;
  right: 5%;
  bottom: 0;
  height: 100%;
  display: none;
  ${({ theme }) => theme.mediaQueries.md} {
    display: inline-block;
  }
`

const BoostFarmQA: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { t } = useTranslation()
  const NftRarityMapping = [
    { desc: t('Legendary'), style: LegendaryColor, factor: '0.25' },
    { desc: t('Epic'), style: EpicColor, factor: '0.15' },
    { desc: t('Rare'), style: RareColor, factor: '0.10' },
    { desc: t('Special'), style: SpecialColor, factor: '0.075' },
    { desc: t('Common'), style: CommonColor, factor: '0.05' },
  ]
  return (
    <Content>
      <StepsContent>
        <StepCard>
          <StepTitle>{t('Step 1')}</StepTitle>
          <StepSubtitle>{t('Stake xVVS token to xVVS vault')}</StepSubtitle>
          <DescText>{t('Lock your xVVS to get staking rewards and boost your farm APR%')}</DescText>
          <StepButtonWrapper
            onClick={() => {
              onDismiss()
              history.push(Path.XVVS_VAULT)
            }}
          >
            <Button variant="secondary">{t('xVVS Vault')}</Button>
          </StepButtonWrapper>
        </StepCard>
        <StepCard>
          <StepTitle>{t('Step 2 (Optional)')}</StepTitle>
          <StepSubtitle>{t('Stake your NFT to enjoy additional APR')}</StepSubtitle>
          <Flex flexDirection="column" justifyContent="space-between" height="100%" gap="10px">
            <DescText>
              {t('After enabling your xVVS vault boost, you can start staking your NFTs for additional boost.')}
            </DescText>
            <DescText>{t('Didn’t purchase Miner Moles NFT yet?')}</DescText>
          </Flex>
          <StepButtonWrapper
            onClick={() => {
              window.open(getVVSMinerMoleMintedUrl())
            }}
          >
            <Button variant="secondary">{t('Marketplace')}</Button>
          </StepButtonWrapper>
        </StepCard>
        <StepCard>
          <StepTitle>{t('Step 3')}</StepTitle>
          <StepSubtitle>{t('Enjoy the double boost')}</StepSubtitle>
          <Flex flexDirection="column" justifyContent="space-between" height="100%" gap="10px">
            <Flex flexDirection="column" gap="10px">
              <DescText>
                {t(
                  'After completing step 1 and 2, you can now enjoy higher boost factor by staking xVVS and NFTs for your farms.',
                )}
              </DescText>
              <DescText>
                {t(
                  'Total boost factor is dependent on your staked liquidity and boost multiplier calculation from staking xVVS and NFTs.',
                )}
              </DescText>
            </Flex>
            <DescText>{t('Simulate your boost calculation:')}</DescText>
          </Flex>
          <StepButtonWrapper
            onClick={() => {
              onDismiss()
              history.push(Path.BOOST_CALCULATOR)
            }}
          >
            <Button variant="secondary">{t('Boost Calculator')}</Button>
          </StepButtonWrapper>
        </StepCard>
      </StepsContent>
      <StakeDetailsContent>
        <StakeDetails>
          <StakeDetailsTitle>{t('Stake xVVS token to xVVS vault')}</StakeDetailsTitle>
          <DescText>
            {t(
              'By locking xVVS in xVVS vaults, you can now earn additional boost APR for your farms (up to %maxNum% max). Calculation of the boost multiplier will depend on the amount of xVVS staked and locked duration.',
              { maxNum: MAX_NUM_OF_BOOST_FARM },
            )}
          </DescText>
        </StakeDetails>
        <StakeDetails>
          <StakeDetailsTitle>{t('Stake NFT to enjoy additional APR')}</StakeDetailsTitle>
          <DescText>
            {t(
              "You can also stake NFT to enjoy higher APR for your farm now!  Just simply enable xVVS vault boost first and you can start staking NFTs from your farm page. You can stake up to %maxNftSelectable% NFTs per farm. Don't miss out the chance to boost with your NFTs now!",
              { maxNftSelectable: MAX_NFT_SELECTABLE },
            )}
          </DescText>
        </StakeDetails>
        <StakeDetails>
          <StakeDetailsTitle>{t('Rarer NFTs bring higher boost multiplier')}</StakeDetailsTitle>
          <DescText>
            {t(
              'Final boost multiplier (Max up to 3x) = xVVS vault multiplier x NFT boost multiplier*. Higher boost multiplier translates to higher boost farm APR.',
            )}
          </DescText>
          <DescText>{t('NFT boost multiplier* :')}</DescText>
          <Flex gap="6px" flexDirection="column">
            {NftRarityMapping.map((item) => {
              return (
                <Flex justifyContent="flex-start" gap="6px" key={item.desc}>
                  <DescText style={{ width: '100px' }} extraStyle={item.style}>
                    {item.desc}
                  </DescText>
                  <DescText>
                    {t('Extra %multiplier%x', {
                      multiplier: item.factor,
                    })}
                  </DescText>
                </Flex>
              )
            })}
          </Flex>
        </StakeDetails>
      </StakeDetailsContent>
      <QuestionBanner>
        <QuestionTitle
          dangerouslySetInnerHTML={{
            __html: t(
              'Still got questions? Find complete details on %openUrlTag%how you can earn from boost farming%closeUrlTag%',
              {
                openUrlTag: '<a href="https://docs.vvs.finance/product-guides/boosted-farming" rel="noreferrer">',
                closeUrlTag: '</a>',
              },
            ),
          }}
        />
        <QuestionBannerMole src="/images/xVVS/miner-mole-banner.png" alt="miner-mole-banner" />
      </QuestionBanner>
    </Content>
  )
}

export default BoostFarmQA
